import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePlayback } from '../../contexts/PlaybackContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatDuration } from '../../utils/formatDuration';

/**
 * MiniPlayer component that shows at the bottom of the screen
 * Allows for persistent playback while navigating through the app
 */
const MiniPlayer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { 
    currentMeditation,
    isPlaying,
    currentTime,
    duration,
    progress,
    togglePlayPause,
    isMiniPlayerVisible,
    hideMiniPlayer,
    stopMeditation
  } = usePlayback();
  
  // Hide mini player on specific pages
  const isOnMeditationPage = location.pathname.startsWith('/meditation/');
  // Check if on configure page (for conditional styling)
  const isOnConfigurePage = location.pathname.startsWith('/meditate');
  // Check if on authentication pages
  const isOnAuthPage = location.pathname === '/login' || 
                      location.pathname === '/register' || 
                      location.pathname.startsWith('/reset-password');

  if (!currentMeditation || !isMiniPlayerVisible || isOnMeditationPage || !user || isOnAuthPage) {
    return null;
  }

  const handleNavigateToPlayer = () => {
    navigate(`/meditation/${currentMeditation.hash}`);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // On the configure page, position the player higher to avoid conflict with navigation buttons
  const playerPositionClass = isOnConfigurePage
    ? "fixed bottom-[72px] left-0 right-0 bg-white dark:bg-grey-800 border-t border-grey-200 dark:border-grey-700 shadow-lg z-10 h-18 sm:h-16"
    : "fixed bottom-0 left-0 right-0 bg-white dark:bg-grey-800 border-t border-grey-200 dark:border-grey-700 shadow-lg z-50 h-20 sm:h-16";
    
  // Handle closing the mini player by stopping playback and hiding the player
  const handleClosePlayer = () => {
    stopMeditation(); // Stop the audio playback
    hideMiniPlayer(); // Hide the mini player UI
  };

  return (
    <div className={playerPositionClass}>
      <div className="container mx-auto max-w-[600px] flex items-center h-full px-4">
        {/* Progress bar - absolute positioned at top of player */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-grey-200 dark:bg-grey-700">
          <div 
            className="h-1 bg-brand-primary" 
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Play/Pause button */}
        <button 
          onClick={togglePlayPause}
          className="flex-shrink-0 w-10 h-10 mr-3 flex items-center justify-center text-brand-primary hover:bg-brand-50 dark:hover:bg-grey-700 rounded-full"
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 5h4v14H6V5zm8 0h4v14h-4V5z" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5.14v14l11-7-11-7z" />
            </svg>
          )}
        </button>
        
        {/* Meditation info - clickable to go to full player */}
        <div 
          className="flex-1 min-w-0 flex flex-col cursor-pointer"
          onClick={handleNavigateToPlayer}
        >
          <p className="font-garamond text-[17px] leading-tight truncate text-grey-900 dark:text-grey-100">
            {currentMeditation.title}
          </p>
          <p className="text-[13px] text-grey-600 dark:text-grey-400 truncate">
            {formatTime(currentTime)} / {formatDuration(duration)}
          </p>
        </div>
        
        {/* Close button */}
        <button 
          onClick={handleClosePlayer}
          className="flex-shrink-0 ml-3 text-grey-500 hover:text-grey-700 dark:text-grey-400 dark:hover:text-grey-200"
          aria-label="Close and stop playback"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default MiniPlayer;
